"use client";

import { useParams } from "next/navigation";
import type { z } from "zod";

import { CtaLink } from "@/components/ui/cta-link";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { getFormattedAmount } from "@/lib/transforms";
import { cn } from "@/lib/utils";
import type { eligibilitySliderSchema } from "@/schemas";

import { useLogSlider } from "./use-log-slider";

type SliderProps = z.infer<typeof eligibilitySliderSchema>["slider"] & {
  heading?: string;
  ctaLabel?: string;
  ctaUrl?: string;
};

export function SliderCard(props: SliderProps) {
  const {
    title,
    heading,
    ctaLabel,
    ctaUrl,
    monthlyRevenue,
    estimatedEligibility,
    footerNote,
    minValue,
    maxValue,
    initialValue,
    lowerMultiplier,
    upperMultiplier,
  } = props;

  const { value, getSliderRootProps, getSliderThumbProps } = useLogSlider({
    initialValue,
    minValue,
    maxValue,
  });
  const { lang: currentLang } = useParams<{ lang: string }>();

  const minEstimation = value * (lowerMultiplier ?? 1.5);
  const maxEstimation = value * (upperMultiplier ?? 3);

  return (
    <div className="rounded-ui-large-xxl flex flex-col gap-5 bg-black p-4 text-white lg:p-8">
      <p className="text-label-10 lg:text-label-12 pb-3 text-white uppercase opacity-40 lg:pb-0">
        {title ?? "Funding calculator"}
      </p>
      {heading && (
        <p className="text-heading-20 lg:text-heading-24 text-white">
          {heading}
        </p>
      )}
      <p className="text-heading-12 lg:text-heading-16 border-t border-white/10 pt-3 text-orange-50">
        {monthlyRevenue ?? "Your monthly revenue"}
      </p>
      <div className="text-body-xl lg:text-data-40 flex items-center justify-between !font-semibold">
        <span className="text-white">
          {getFormattedAmount({ amount: value, lang: currentLang })}
          {value === maxValue && "+"}
        </span>
      </div>
      <div className="my-4 flex w-full items-center">
        <div className="bg-pulse-500 h-px w-[10%]" />
        <SliderRoot
          {...getSliderRootProps()}
          className={cn("w-4/5")}
          rangeClassName="bg-pulse-500"
        >
          <SliderThumb
            {...getSliderThumbProps()}
            aria-label="monthly average revenue"
            className="focus-visible:ring-border-info hover:bg-pulse-500/10 ml-[2px] flex size-[44px] scale-150 cursor-pointer items-center justify-center rounded-full bg-transparent focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:outline-none"
          >
            <span className="bg-pulse-500 size-5 rounded-full" />
          </SliderThumb>
        </SliderRoot>
        <div className="h-px w-[10%] bg-white/20" />
      </div>
      <p className="text-heading-12 lg:text-heading-16 border-t border-white/10 pt-3 text-orange-50">
        {estimatedEligibility ?? "Your funding estimate"}
      </p>
      <div className="text-body-xl lg:text-data-40 text-pulse-500 !font-semibold">
        {getFormattedAmount({ amount: minEstimation, lang: currentLang })}
        {"  -  "}
        {getFormattedAmount({ amount: maxEstimation, lang: currentLang })}
        {value === maxValue && "+"}
      </div>
      {ctaLabel && ctaUrl && (
        <CtaLink
          id="blog-calculator-cta"
          className="mt-4 w-fit gap-2 px-4 py-3"
          href={ctaUrl}
          external
          variant="default"
          size="xl"
        >
          {ctaLabel}
        </CtaLink>
      )}
      <div
        className={cn(
          "text-body-2xs lg:text-body-xs mt-4 text-white opacity-80",
          !ctaLabel && "lg:mt-[148px]"
        )}
      >
        {footerNote ??
          "*Estimate only. Offers are subject to underwriting review."}
      </div>
    </div>
  );
}
